import { fetchReportData, fetchSignedReport } from '@services/reportService';
import type { ILoginPayload } from '@services/types';
import { clearSessionReport, getSessionReport, storeSessionReport } from '@store/helpers';
import { create } from 'zustand';

import type { IReportStore } from './types';

const defaultReportState = { report: null, loading: false, error: null };

export const useReportStore = create<IReportStore>((set, get) => {
  return {
    ...defaultReportState,
    setReport: (report) => {
      set({ report });
    },
    loadReport: async (payload: ILoginPayload) => {
      if (get().report || get().loading) {
        return false;
      }

      set({
        loading: true,
        error: null,
      });
      try {
        const signedReport = await fetchSignedReport(payload);

        // Store signed report in session storage to keep the session alive on page reload
        storeSessionReport(payload.po_number, signedReport);

        // Fetch actual report content using signed URL
        const report = await fetchReportData(signedReport.url);

        set({
          report: {
            ...report,
            id: payload.po_number,
          },
          loading: false,
          error: null,
        });

        return true;
      } catch (error) {
        let message = 'Failed to load report';

        if (error instanceof Error && error.message) {
          message = error.message;
        }

        set({
          error: message,
          loading: false,
        });

        return false;
      }
    },
    restoreReport: async (poNumber) => {
      set({
        loading: true,
        error: null,
      });

      try {
        const signedReport = getSessionReport(poNumber);

        if (!signedReport) {
          throw new Error('Session expired');
        }

        const report = await fetchReportData(signedReport.url);
        set({
          report: { ...report, id: poNumber },
          loading: false,
          error: null,
        });
      } catch (error) {
        clearSessionReport(poNumber);
        set({
          error: error instanceof Error ? error.message : 'Failed to restore report',
          loading: false,
          report: null,
        });
      }
    },
    reportStoreReset: () => {
      set(defaultReportState);
    },
  };
});
